// Capture receipt presentation. Reuses @secops/guard-core's receipt vocabulary (ScanReceipt shape +
// tone→role) so Guard Web reads identically to the native app. guard-core is consumed via the
// monorepo's deep-relative .ts convention (the same way apps/mobile imports @secops/shared) — the
// package-name import isn't bundler-resolvable, but the relative .ts path resolves cleanly in Vite + tsc.

import {
  describeScanReceipt,
  scanReceiptToneRole,
  type ScanReceipt,
  type ScanReceiptTone,
} from "../../../../packages/guard-core/src/scan-receipt.ts";
import type { ScanMethod, ScanOutcome } from "./scan-contract";

export type { ScanReceipt, ScanReceiptTone };
export { scanReceiptToneRole };

/** Map a server scan outcome (or a queue/submit event) to a guard-facing receipt. */
export function captureReceipt(
  result:
    | { kind: "outcome"; outcome: ScanOutcome }
    | { kind: "submit-failure" }
    | { kind: "queued-offline" }
    | { kind: "queued-retry"; reason?: string },
  method: ScanMethod,
): ScanReceipt {
  if (result.kind === "queued-offline") {
    return describeScanReceipt({ outcome: "queued-offline", method });
  }
  if (result.kind === "queued-retry") {
    return describeScanReceipt({
      outcome: "queued-retry",
      method,
      ...(result.reason !== undefined ? { reason: result.reason } : {}),
    });
  }
  if (result.kind === "submit-failure") {
    return {
      tone: "pending",
      title: "Not submitted",
      detail: "Couldn't reach the server. Please try again.",
      method,
    };
  }
  switch (result.outcome) {
    case "validated":
    case "flagged":
    case "duplicate":
    case "rejected":
      return describeScanReceipt({ outcome: result.outcome, method });
  }
}
